/* eslint-disable react/jsx-props-no-spreading -- the actions take the props of the post panel */
import React from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import type { RolloutDayKey, RolloutKind } from 'fym-shared/src/music24/releaseRollout';
import { TypedMemo } from '../../../common/types';
import { platformName } from '../platformMeta';
import { rolloutDayTitle } from '../releases/plan/rolloutDayCopy';
import { calendarStateOf } from './calendarFilterModel';
import { postThumbUrl, songArtworkUrl, songColourStyle } from './calendarPostView';
import { DayPanelActions } from './DayPanelActions';
import type { PostStateActionsProps } from './dayPanelTypes';
import { formatMoveTime, postStateWord } from './postCardLabel';
import { PostAiNote } from './PostAiNote';
import { PostCaption } from './PostCaption';
import { PostSettingsNote } from './PostSettingsNote';
import { PostXLimitNote } from './PostXLimitNote';
import { useDisplayPostStatus } from '../displayPostStatus';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the panel; each part uses its own slice of it */
import styles from './DayPanel.module.scss';
/* eslint-enable css-modules/no-unused-class */

const PILL_CLASS = {
  needsYou: styles.pillManual,
  failed: styles.pillFailed,
  draft: styles.pillDraft,
  scheduled: styles.pillScheduled,
  published: styles.pillPosted,
  canceled: styles.pillDraft,
} as const;

/** The step of the plan of a post ("Teaser", "AI reel, day 5"), from its rollout key `<kind>.<day key>`. */
function planStep(rolloutKey: string | null): { kind: RolloutKind; key: RolloutDayKey } | null {
  if (rolloutKey === null) {
    return null;
  }
  const [kind, key] = rolloutKey.split('.');
  if ((kind !== 'release' && kind !== 'spike' && kind !== 'evergreen' && kind !== 'reels') || key === undefined) {
    return null;
  }
  return { kind, key: key as RolloutDayKey };
}

/** The open post of the day panel: the media, the facts and the actions of its state. */
const DayPanelPostComponent: React.FC<PostStateActionsProps> = (props) => {
  const intl = useIntl();
  const { post, song, xPosts } = props;
  const state = calendarStateOf(post.status);
  // A scheduled post whose time has passed is going out: it shows as publishing.
  const shownStatus = useDisplayPostStatus(post);
  const picture = postThumbUrl(post, song);
  const step = planStep(post.rolloutKey);
  const cover = songArtworkUrl(song);
  const timeLabel =
    state === 'published'
      ? intl.formatMessage({ id: 'app.sml.promote.calendar.kv.wentOut', defaultMessage: 'Went out' })
      : state === 'failed' || state === 'needsYou'
        ? intl.formatMessage({ id: 'app.sml.promote.post.wasDue', defaultMessage: 'Was due' })
        : intl.formatMessage({ id: 'app.sml.promote.post.scheduledAt', defaultMessage: 'Goes out' });
  const at = new Date(state === 'published' && post.postedAt !== null ? post.postedAt : post.scheduledAt);

  return (
    <div className={styles.dpPost} data-testid={`day-panel-post-${post.uuid}`}>
      <div className={styles.dpMedia}>
        {picture === null ? (
          <div className={clsx(styles.big, styles.bigText)}>{post.caption.slice(0, 80)}</div>
        ) : (
          <img className={styles.big} src={picture} alt="" />
        )}
        <dl className={styles.kvs}>
          <span className={clsx(styles.pill2, PILL_CLASS[state])}>
            {postStateWord(intl, { ...post, status: shownStatus })}
          </span>
          <div>
            <dt>{intl.formatMessage({ id: 'app.sml.promote.calendar.kv.channel', defaultMessage: 'Channel' })}</dt>
            <dd>{platformName(post.platform)}</dd>
          </div>
          <div>
            <dt>{timeLabel}</dt>
            <dd>{formatMoveTime(intl, at)}</dd>
          </div>
          {song === undefined ? null : (
            <div>
              <dt>{intl.formatMessage({ id: 'app.sml.promote.calendar.kv.song', defaultMessage: 'Song' })}</dt>
              <dd className={styles.songchip} style={songColourStyle(song)}>
                {cover === null ? (
                  <span className={styles.songCover} />
                ) : (
                  <img className={styles.songCover} src={cover} alt="" />
                )}
                {song.title}
              </dd>
            </div>
          )}
          {step === null ? null : (
            <div>
              <dt>{intl.formatMessage({ id: 'app.sml.promote.calendar.kv.plan', defaultMessage: 'From the plan' })}</dt>
              <dd>{rolloutDayTitle(intl, step.kind, step.key)}</dd>
            </div>
          )}
        </dl>
      </div>
      {/* The "needs you" tools show the caption with its copy button. */}
      {state === 'needsYou' ? null : <PostCaption caption={post.caption} />}
      <PostXLimitNote post={post} xPosts={xPosts ?? null} />
      <PostSettingsNote post={post} />
      <PostAiNote post={post} />
      <DayPanelActions {...props} />
    </div>
  );
};

export const DayPanelPost = TypedMemo(DayPanelPostComponent);
